import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';

import type { RemixContentComponentProps } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import {
	RemixContainer,
	type RemixOptionSelectionParameters,
} from '@atlassian/malleable-ui-components';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { useRemixToAIFC } from './useRemixToAIFC.ts';
import type { DynamicUiSource } from './common-types.ts';

/**
 * Creates a content component for RemixContentComponent using RemixContainer.
 */
export const createRemixContentComponent = () => {
	const Component = ({
		handleModalClose,
		selectedContext,
		editorApi,
		onStartThinking,
		onStopThinking,
	}: RemixContentComponentProps) => {
		const { sendAIFCPrompt, stopRemixing, isRovoResponding, openAIFCStagingModal } =
			useRemixToAIFC();
		const { showSmartCreationModal } = useSmartCreationModalState();

		const isRemixCancelled = useRef(false);

		const onRemixFlowActiveChange = useCallback(
			(active: boolean) => {
				if (active) {
					isRemixCancelled.current = false;
					onStartThinking();
				} else {
					isRemixCancelled.current = true;
					stopRemixing();
					onStopThinking({ reopenModal: true });
				}
			},
			[onStartThinking, stopRemixing, onStopThinking],
		);

		const prevIsRovoResponding = useRef(false);
		useEffect(() => {
			// Only open the staging modal when the smart creation finished processing the prompt
			if (!isRemixCancelled.current && !prevIsRovoResponding.current && isRovoResponding) {
				openAIFCStagingModal();
			}
			prevIsRovoResponding.current = isRovoResponding;
		}, [isRovoResponding, openAIFCStagingModal]);

		const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
			const localIds = selectedContext?.localIds ?? [];
			const startLocalId = localIds[0] ?? '';
			const endLocalId = localIds[localIds.length - 1] ?? '';
			const fragmentAdf = selectedContext?.selectionFragment;

			let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
			if (editorApi) {
				fullAdf = await new Promise((resolve) => {
					try {
						editorApi.core?.actions?.requestDocument?.(
							(doc: any) => {
								resolve(doc as DocNode);
							},
							{ alwaysFire: true },
						);
					} catch {
						resolve({ type: 'doc', version: 1, content: [] });
					}
				});
			}

			return {
				contentType: 'page' as const,
				startLocalId,
				endLocalId,
				startIndex: selectedContext?.startIndex ?? 0,
				endIndex: selectedContext?.endIndex ?? -1,
				fragmentAdf,
				fullAdf,
			};
		}, [editorApi, selectedContext]);

		const onOptionSelect = useCallback(
			async ({ type, userPrompt }: RemixOptionSelectionParameters) => {
				const dynamicUiSource = await constructDynamicUiSource();
				void sendAIFCPrompt({
					userPrompt,
					dynamicUiType: type,
					dynamicUiSource,
				});
			},
			[constructDynamicUiSource, sendAIFCPrompt],
		);

		return (
			<RemixContainer
				selectedText={selectedContext.selectionMarkdown ?? ''}
				onClose={handleModalClose}
				onOptionSelect={onOptionSelect}
				onRemixFlowActiveChange={onRemixFlowActiveChange}
				isEphemeralViewVisible={showSmartCreationModal}
			/>
		);
	};

	const Wrapper = (props: RemixContentComponentProps) => {
		const { onStopThinking, isThinking, isModalOpen } = props;
		const { showSmartCreationModal } = useSmartCreationModalState();
		const isSmartCreationModalOpen = useRef(false);

		useLayoutEffect(() => {
			// Selected content should remain highlighted throughout the entire flow even when ephemeral modal is open
			// We deselect the content when the ephemeral modal is closed
			if (isSmartCreationModalOpen.current && !showSmartCreationModal && isThinking) {
				onStopThinking();
			}
			isSmartCreationModalOpen.current = showSmartCreationModal;
		}, [showSmartCreationModal, isThinking, onStopThinking]);

		return isModalOpen || isThinking ? (
			<ConfluenceConversationAssistant>
				<Component {...props} />
			</ConfluenceConversationAssistant>
		) : null;
	};

	return Wrapper;
};
